/**
 * The fauna column next to the viewer: one card per animal of the displayed
 * image, in the order the analysis gives (sharpest first). The thumbnail comes
 * in raw (unrotated) orientation and is turned here like the viewer, so all
 * rotation lives in the front. The before/after blink, when there is one,
 * plays permanently in place of the still thumbnail; clicking a card zooms
 * the viewer onto the animal. Users allowed to correct the
 * analysis get a pencil next to the taxon name, on hover, to change it.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BugIcon, ImageOffIcon, PencilIcon } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {
    FaunaAnimal,
    FaunaTaxon,
    taxonLabel,
    taxonName,
    useFaunaPermissions,
    useImageFauna,
} from '@/services/fauna.service';
import TaxonDialog from './TaxonDialog';
import { GhostCards, PendingNote } from './AnalysisPending';

function AnimalCard({
    animal,
    taxa,
    selected,
    canEdit,
    rotation,
    onSelect,
    onEdit,
}: {
    animal: FaunaAnimal;
    taxa: FaunaTaxon[];
    selected: boolean;
    canEdit: boolean;
    /** Degrees, as the viewer is turned. */
    rotation: number;
    onSelect: () => void;
    onEdit: () => void;
}) {
    const { t } = useTranslation();
    const src = animal.blinkUrl ?? animal.thumbnailUrl;
    return (
        <figure className="flex flex-col gap-1">
            <button
                type="button"
                onClick={onSelect}
                className={`aspect-square w-full cursor-zoom-in overflow-hidden rounded-md bg-[#1c2620] transition-shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2d6a4f] ${
                    selected
                        ? 'ring-2 ring-[#2d6a4f]'
                        : 'ring-1 ring-[#dadbd5] hover:ring-2 hover:ring-[#2d6a4f]'
                }`}
            >
                {src ? (
                    <img
                        src={src}
                        alt={taxonLabel(animal.taxon)}
                        className="h-full w-full object-cover"
                        // Square, so a quarter-turn stays inside the card.
                        style={{ transform: `rotate(${rotation}deg)` }}
                        loading="lazy"
                    />
                ) : (
                    <div className="flex h-full w-full flex-col items-center justify-center gap-1 px-2 text-center text-[10px] text-[#a8aea3]">
                        <ImageOffIcon className="h-4 w-4" />
                        {t('Thumbnail pending')}
                    </div>
                )}
            </button>
            <figcaption className="px-0.5 text-[11px] leading-tight text-[#1c2620]">
                <span className="group/taxon flex items-center gap-1">
                    <span
                        className="truncate font-medium italic"
                        title={taxonName(animal.taxon, taxa)}
                    >
                        {taxonLabel(animal.taxon)}
                    </span>
                    {canEdit && (
                        <button
                            type="button"
                            onClick={onEdit}
                            title={t('Change taxon')}
                            className="shrink-0 rounded p-0.5 text-[#7a8077] opacity-0 transition-opacity hover:bg-[#f4f5f1] hover:text-[#1c2620] focus:opacity-100 group-hover/taxon:opacity-100"
                        >
                            <PencilIcon className="h-3 w-3" />
                        </button>
                    )}
                </span>
                {animal.taxon_note && (
                    <span className="block truncate text-[#7a8077]">
                        {animal.taxon_note}
                    </span>
                )}
                {animal.last_edit && (
                    <span className="block truncate text-[10px] text-[#a8aea3]">
                        {t('Edited by {{name}}', {
                            name: animal.last_edit.by.name,
                        })}
                    </span>
                )}
            </figcaption>
        </figure>
    );
}

export default function FaunaPanel({
    projectId,
    imageId,
    selectedAnimalId,
    pending = false,
    playing = false,
    rotation,
    onSelect,
}: {
    projectId?: string;
    imageId?: string;
    selectedAnimalId?: string;
    /** Degrees the viewer is turned by; the thumbnails follow it. */
    rotation: number;
    /** The sample has no fauna analysis yet, on any date. */
    pending?: boolean;
    /** The timelapse is playing: cards flashing by at every frame are hard
     *  on the eyes, so they wait for the pause. */
    playing?: boolean;
    onSelect: (animal: FaunaAnimal) => void;
}) {
    const { t } = useTranslation();
    const { data: fauna, isLoading, isError } = useImageFauna(
        projectId,
        imageId,
        !pending && !playing,
    );
    const { data: permissions } = useFaunaPermissions(projectId);
    const canEdit = permissions?.canEdit === true;
    const [editing, setEditing] = useState<FaunaAnimal | null>(null);

    let body: React.ReactNode;
    if (pending) {
        body = (
            <GhostCards>
                <PendingNote>
                    {t('The fauna analysis will appear here.')}
                </PendingNote>
            </GhostCards>
        );
    } else if (playing) {
        body = <GhostCards />;
    } else if (isLoading) {
        body = (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(104px,1fr))] gap-2">
                {Array.from({ length: 6 }, (_, i) => (
                    <Skeleton
                        key={i}
                        className="aspect-square w-full rounded-md"
                    />
                ))}
            </div>
        );
    } else if (isError) {
        body = <Empty>{t('Unable to load the fauna analysis')}</Empty>;
    } else if (!fauna) {
        // Not analysed yet: the empty placeholder says enough on its own.
        body = <GhostCards />;
    } else if (fauna.animals.length === 0) {
        body = <Empty>{t('No animal on this image')}</Empty>;
    } else {
        body = (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(104px,1fr))] gap-x-2 gap-y-3">
                {fauna.animals.map((animal) => (
                    <AnimalCard
                        key={animal.id}
                        animal={animal}
                        taxa={fauna.taxa}
                        selected={animal.id === selectedAnimalId}
                        canEdit={canEdit}
                        rotation={rotation}
                        onSelect={() => onSelect(animal)}
                        onEdit={() => setEditing(animal)}
                    />
                ))}
            </div>
        );
    }

    return (
        <div className="flex h-full min-w-0 flex-1 flex-col rounded-lg bg-white ring-1 ring-[#e3e5df]">
            <div className="flex shrink-0 items-center border-b border-[#ecede8] px-3 py-2">
                <h3 className="flex items-center gap-1.5 text-[13px] font-semibold text-[#1c2620]">
                    <BugIcon className="h-3.5 w-3.5 text-[#2d6a4f]" />
                    {t('Fauna')}
                </h3>
            </div>
            {/* Scrolls on its own when the cards overflow the image's height. */}
            <div className="min-h-0 flex-1 overflow-y-auto p-2">{body}</div>
            {fauna && editing && (
                <TaxonDialog
                    projectId={projectId}
                    imageId={imageId}
                    animal={editing}
                    taxa={fauna.taxa}
                    onClose={() => setEditing(null)}
                />
            )}
        </div>
    );
}

function Empty({ children }: { children: React.ReactNode }) {
    return (
        <p className="px-2 py-8 text-center text-xs text-[#7a8077]">
            {children}
        </p>
    );
}
